import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { canStartKarkhanaTrial, canUseKarkhana, canUseRounds, isKarkhanaSupervisor } from "../../helpers/karkhanaEnabled";
import { startStudioModuleTrial } from "../../helpers/studioEntitlementsApi";
import { isStudioTrialActive, moduleTrialEndsAtMs } from "../../helpers/studioModules";
import notifications from "../../common/Components/Notifications/Notification";
import { useStudioOrgBrand } from "../../common/StudioOrgBrandContext";
import { getApprovals, getKarkhanaDashboard, getNotCounted } from "../../services/karkhanaApi";
import CashToCheckAlert from "./cashbook/owner/CashToCheckAlert";
import "../gi-registry/gi-registry.scss";
import KarkhanaDashboard from "./home/KarkhanaDashboard";
import LoomBoard, { localDay } from "./home/LoomBoard";
import { nextLoomRange } from "./home/loomCodes";
import { AddLoomsSheet, LoomRangeForm } from "./home/AddLooms";
import { LangToggle, useKT } from "./i18n/kt";
import "./home/karkhana-home.scss";
import { kp } from "./karkhanaPaths";
import { OutLink } from "./ui/leaveKarkhana";

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

/** Karkhana's own free trial (an existing studio that started it on first open). */
const KarkhanaTrialBanner = () => {
  const ends = moduleTrialEndsAtMs("KARKHANA");
  if (!ends || isStudioTrialActive()) return null;
  const days = Math.max(1, Math.ceil((ends - Date.now()) / 86400000));
  return (
    <p className="kh-trial-banner" role="status">
      <strong>
        Free trial: {days} day{days === 1 ? "" : "s"} left.
      </strong>{" "}
      To keep Karkhana after that, go to <OutLink to="/studio/modules">Profile → Studio modules</OutLink>. Your records
      stay either way.
    </p>
  );
};

const WorkIcon = ({ light }: { light?: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={light ? "#fff" : "currentColor"} strokeWidth="2" aria-hidden="true">
    <rect x="3" y="7" width="18" height="10" rx="2" />
    <path d="M7 7v4M11 7v3M15 7v4M19 7v3" />
  </svg>
);

const PeopleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20c.8-3.5 3.4-5.5 6.5-5.5s5.7 2 6.5 5.5" />
    <path d="M16 11l2 2 4-4" />
  </svg>
);

/** A studio with no looms yet: one question, then tap a loom. */
const FirstLooms = ({ onAdded, canImport }: { onAdded: () => void; canImport: boolean }) => {
  const { t } = useKT();
  const input = React.useRef<HTMLInputElement>(null);
  return (
    <>
      <div className="kh-start">
        <h3>{t("Start in 2 steps")}</h3>
        <p>{t("1. Add your looms. 2. Tap a loom to start its saree. Weavers and helpers are added right there.")}</p>
        <LoomRangeForm id="kh-first-looms" inputRef={input} onAdded={onAdded} />
        {canImport ? (
          <Link className="kh-start__import" to={kp.import()}>
            {t("Records already in a sheet or register? Import them")} ›
          </Link>
        ) : null}
      </div>
      {/* The board it will fill: empty for now, with one tile that goes back to the question. */}
      <div className="kb__head">
        <h3>{t("Looms")}</h3>
        <span className="kb__hint">0</span>
      </div>
      <div className="kb__grid">
        <button type="button" className="kb-tile is-idle kh-start__add" onClick={() => input.current?.focus()}>
          + {t("Add")}
        </button>
      </div>
    </>
  );
};

/**
 * Karkhana home: the two places work is entered (Daily work, Attendance), what is waiting for the
 * owner, and every loom as a coloured tile. Everything else is in More.
 */
const KarkhanaHome = () => {
  const { t } = useKT();
  const history = useHistory();
  const role = readAuthUser()?.users?.role;
  const supervisor = isKarkhanaSupervisor(role);
  const [, setAuthTick] = useState(0);
  const usable = supervisor ? canUseRounds(role) : canUseKarkhana(role);
  const [data, setData] = useState<any>(null);
  const [waiting, setWaiting] = useState<any>(null);
  const [late, setLate] = useState<number>(0);
  const [details, setDetails] = useState(false);
  const [addingLooms, setAddingLooms] = useState(false);
  const { name: studioName } = useStudioOrgBrand();

  useEffect(() => {
    if (usable || supervisor) return;
    if (!canStartKarkhanaTrial(role)) {
      history.replace("/dashboard");
      return;
    }
    // First open by a studio that never had Karkhana starts its free trial; no extra click.
    let live = true;
    startStudioModuleTrial("KARKHANA")
      .then((entitled) => {
        if (!live) return;
        if (entitled) setAuthTick((n) => n + 1);
        else history.replace("/studio/modules");
      })
      .catch(() => live && history.replace("/dashboard"));
    return () => {
      live = false;
    };
  }, [usable, supervisor, role, history]);

  const load = useCallback(() => {
    getKarkhanaDashboard()
      .then((res: any) => setData(res?.success ? res.data : { looms: [] }))
      .catch(() => setData({ looms: [] }));
    getNotCounted()
      .then((res: any) => setWaiting(res?.success ? res.data : null))
      .catch(() => setWaiting(null));
    if (!supervisor) {
      getApprovals()
        .then((res: any) => {
          const d = res?.success ? res.data : null;
          setLate(d ? (d.entries?.length || 0) + (d.closures?.length || 0) : 0);
        })
        .catch(() => setLate(0));
    }
  }, [supervisor]);

  useEffect(() => {
    if (usable) load();
  }, [usable, load]);

  if (!usable) {
    return canStartKarkhanaTrial(role) ? (
      <div className="gi-registry-page kw-page">
        <p className="kh-trial-banner" role="status">
          Starting your free Karkhana trial…
        </p>
      </div>
    ) : null;
  }

  const today = localDay();
  const looms: any[] = data?.looms || [];
  // "Done" counts looms with a saree on them: closed and empty looms are neither done nor left.
  const running = looms.filter((l) => l.status !== "IDLE" && l.status !== "CLOSED" && l.sareeNumber);
  const doneToday = running.filter((l) => l.lastEntry === today).length;
  const pt = data?.people?.totals || {};
  const inNow = Number(pt.inNow ?? 0);
  const outNow = Math.max(0, Number(pt.presentToday ?? 0) - inNow);
  const designsWaiting = new Set((waiting?.loom || []).map((g: any) => g.designId)).size;
  const entriesWaiting = (waiting?.loom || []).reduce((n: number, g: any) => n + (Number(g.entries) || 0), 0);
  const peopleWaiting = waiting?.time?.length || 0;
  const rateWait = entriesWaiting + peopleWaiting;

  return (
    <div className="gi-registry-page kw-page kh-home">
      <KarkhanaTrialBanner />
      <header className="kh-home__head">
        <div>
          <h2>{t("Karkhana")}</h2>
          <span>
            {studioName ? `${studioName} · ` : ""}
            {new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
          </span>
        </div>
        <LangToggle />
      </header>

      {data && !looms.length ? (
        <FirstLooms onAdded={load} canImport={!supervisor} />
      ) : (
        <>
          <div className="kh-entry">
            <Link to={kp.dailyWork()} className="kh-entry__btn is-primary">
              <WorkIcon light />
              <strong>{t("Daily work")}</strong>
              <span>{data ? t("{{done}} of {{n}} looms done", { done: doneToday, n: running.length }) : "…"}</span>
            </Link>
            <Link to={kp.attendance()} className="kh-entry__btn">
              <PeopleIcon />
              <strong>{t("Attendance")}</strong>
              <span>{data ? outNow ? t("{{n}} in · {{out}} out", { n: inNow, out: outNow }) : t("{{n}} in now", { n: inNow }) : "…"}</span>
            </Link>
          </div>

          {rateWait ? (
            supervisor ? (
              <div className="kh-alert is-info" role="status">
                {t("{{n}} waiting for a rate. The owner has been told. Work is saved.", { n: rateWait })}
              </div>
            ) : (
              <Link to={kp.pay(undefined, "not-counted")} className="kh-alert is-warn">
                <span>
                  <strong>
                    {[
                      designsWaiting ? t(designsWaiting === 1 ? "1 design has no rate." : "{{n}} designs have no rate.", { n: designsWaiting }) : "",
                      peopleWaiting ? t(peopleWaiting === 1 ? "1 person has no day rate." : "{{n}} people have no day rate.", { n: peopleWaiting }) : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </strong>{" "}
                  {entriesWaiting
                    ? t(entriesWaiting === 1 ? "1 entry is waiting to be paid." : "{{n}} entries are waiting to be paid.", { n: entriesWaiting })
                    : t("Work is saved; it is paid once the rate is filled.")}
                </span>
                <span className="kh-alert__btn">{t("Fill rate")}</span>
              </Link>
            )
          ) : null}
          {!supervisor ? <CashToCheckAlert /> : null}
          {!supervisor && late ? (
            <Link to={kp.approvals()} className="kh-alert is-info">
              <span>
                <strong>{t("{{n}} late entries", { n: late })}</strong> {t("wait for your OK.")}
              </span>
              <span className="kh-alert__btn is-line">{t("Review")}</span>
            </Link>
          ) : null}

          {data ? <LoomBoard looms={looms} today={today} onAdd={() => setAddingLooms(true)} /> : <div className="kw-empty">{t("Loading…")}</div>}
          {addingLooms ? (
            <AddLoomsSheet
              hint={nextLoomRange(looms.map((l) => l.code))}
              onClose={() => setAddingLooms(false)}
              onAdded={() => {
                setAddingLooms(false);
                load();
              }}
            />
          ) : null}

          {!supervisor && data ? (
            <div className="kh-details">
              <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setDetails(!details)} aria-expanded={details}>
                {details ? t("Hide details") : t("Details: progress, cost and people")}
              </button>
              {details ? <KarkhanaDashboard /> : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default KarkhanaHome;
